import React from "react";
import Media from "react-media";
import Icon from "../../components/Icon";
import constants from "../../../utils/constants";
import { useTranslation } from "next-i18next";
import Link from "next/link";
import LinkRender from "../LinkRender/LinkRender";
import { MenuRoute } from "../../../types/MenuRoute";
import { isMobile } from "mobile-device-detect";

type FooterProps = {
  routes: MenuRoute[];
  lang: string;
};

const Footer: React.FunctionComponent<FooterProps> = ({
  routes,
  lang,
}): React.ReactElement => {
  const { t } = useTranslation();
  const parsedRoutes = routes.filter(
    (route: MenuRoute) =>
      route.positions.includes(constants.MENU_POSITIONS.BOTTOM) && route.names
  );
  const routesToSplit = parsedRoutes;
  const halfWayThough = Math.floor(routesToSplit.length / 2);

  const routesLeft = routesToSplit.slice(0, halfWayThough);
  const routesRight = routesToSplit.slice(halfWayThough, routesToSplit.length);

  const NEXT_PUBLIC_INSTAGRAM_URL =
    process.env.NEXT_PUBLIC_INSTAGRAM_URL ?? null;
  const NEXT_PUBLIC_LINKEDIN_URL = process.env.NEXT_PUBLIC_LINKEDIN_URL ?? null;
  const NEXT_PUBLIC_FACEBOOK_URL = process.env.NEXT_PUBLIC_FACEBOOK_URL ?? null;
  const NEXT_PUBLIC_TWITTER_URL = process.env.NEXT_PUBLIC_TWITTER_URL ?? null;
  const NEXT_PUBLIC_YOUTUBE_URL = process.env.NEXT_PUBLIC_YOUTUBE_URL ?? null;

  const currentYear = new Date().getFullYear();

  return (
    <div>
      <Media query="(min-width: 768px)" defaultMatches>
        <div className="footer">
          <div className="topContainer">
            {parsedRoutes.map((item) => (
              <LinkRender
                className="topContainer__text"
                lang={lang}
                item={item}
                key={item.id}
              />
            ))}
          </div>
          <div className="bottomContainer">
            <div className="iconsContainer">
              {NEXT_PUBLIC_INSTAGRAM_URL && (
                <a
                  href={NEXT_PUBLIC_INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="icon"
                >
                  <Icon icon="INSTAGRAM" width="32px" color="white-icon" />
                </a>
              )}
              {NEXT_PUBLIC_LINKEDIN_URL && (
                <a
                  href={NEXT_PUBLIC_LINKEDIN_URL}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="icon"
                >
                  <Icon icon="LINKEDIN" width="32px" color="white-icon" />
                </a>
              )}
              {NEXT_PUBLIC_FACEBOOK_URL && (
                <a
                  href={NEXT_PUBLIC_FACEBOOK_URL}
                  rel="noopener noreferrer nofollow"
                  target="_blank"
                  className="icon"
                >
                  <Icon icon="FACEBOOK" width="32px" color="white-icon" />
                </a>
              )}
              {NEXT_PUBLIC_TWITTER_URL && (
                <a
                  href={NEXT_PUBLIC_TWITTER_URL}
                  rel="noopener noreferrer nofollow"
                  target="_blank"
                  className="icon"
                >
                  <Icon icon="TWITTER" width="32px" color="white-icon" />
                </a>
              )}
              {NEXT_PUBLIC_YOUTUBE_URL && (
                <a
                  href={NEXT_PUBLIC_YOUTUBE_URL}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="icon"
                >
                  <Icon icon="YT" width="32px" color="white-icon" />
                </a>
              )}
            </div>

            <a
              href="https://coderslab.pl"
              target="_blank"
              rel="noopener noreferrer"
              className="bottomContainer__text bottomContainer__text--first"
            >
              {t("footer-copy")}
              {currentYear}
            </a>
            <Link passHref href={`/${lang}${t("routes-rules")}`}>
              <a className="bottomContainer__text">{t("footer-rules")}</a>
            </Link>
            <Link passHref href={`/${lang}${t("routes-privacy")}`}>
              <a className="bottomContainer__text">{t("footer-policy")}</a>
            </Link>
          </div>
        </div>
      </Media>

      <Media query="(max-width: 767px)" defaultMatches={isMobile}>
        <div className="footer">
          <div className="topContainer">
            <div className="topContainer__column topContainer__column--left">
              {routesLeft.map((item) => (
                <LinkRender
                  className="topContainer__text"
                  lang={lang}
                  item={item}
                  key={item.id}
                />
              ))}
              <Link passHref href={`/${lang}${t("routes-privacy")}`}>
                <a className="topContainer__text">{t("footer-policy")}</a>
              </Link>
            </div>
            <div className="topContainer__column topContainer__column--right">
              {routesRight.map((item) => (
                <LinkRender
                  className="topContainer__text"
                  lang={lang}
                  item={item}
                  key={item.id}
                />
              ))}
              <Link passHref href={`/${lang}${t("routes-rules")}`}>
                <a className="topContainer__text">{t("footer-rules")}</a>
              </Link>
            </div>

            <div className="iconsContainer">
              {NEXT_PUBLIC_INSTAGRAM_URL && (
                <a
                  href={NEXT_PUBLIC_INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="icon"
                >
                  <Icon icon="INSTAGRAM" width="32px" color="white-icon" />
                </a>
              )}
              {NEXT_PUBLIC_LINKEDIN_URL && (
                <a
                  href={NEXT_PUBLIC_LINKEDIN_URL}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="icon"
                >
                  <Icon icon="LINKEDIN" width="32px" color="white-icon" />
                </a>
              )}
              {NEXT_PUBLIC_FACEBOOK_URL && (
                <a
                  href={NEXT_PUBLIC_FACEBOOK_URL}
                  rel="noopener noreferrer nofollow"
                  target="_blank"
                  className="icon"
                >
                  <Icon icon="FACEBOOK" width="32px" color="white-icon" />
                </a>
              )}
              {NEXT_PUBLIC_TWITTER_URL && (
                <a
                  href={NEXT_PUBLIC_TWITTER_URL}
                  rel="noopener noreferrer nofollow"
                  target="_blank"
                  className="icon"
                >
                  <Icon icon="TWITTER" width="32px" color="white-icon" />
                </a>
              )}
              {NEXT_PUBLIC_YOUTUBE_URL && (
                <a
                  href={NEXT_PUBLIC_YOUTUBE_URL}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="icon"
                >
                  <Icon icon="YT" width="32px" color="white-icon" />
                </a>
              )}
            </div>
          </div>
          <div className="bottomContainer">
            <span className="bottomContainer__text bottomContainer__text--first">
              {t("footer-copy")}
            </span>
          </div>
        </div>
      </Media>
    </div>
  );
};

export default Footer;
